import { QueryParameter } from "./QueryParameter";
import { IStringDictionary } from "Core/Interfaces";
import { PageContext } from "./PageContext";

/**
 * Pass additional custom parameters to DMS commands using this class.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // get an application parameter
 * var param = DMS.ApplicationParameter.get('param1');
 *
 * // set an application parameter
 * DMS.ApplicationParameter.set('param1', 'test');
 * ```
 */
export class ApplicationParameter {
    private static _applicationParameters: IStringDictionary<any> = {};

    /**
     * @hidden
     */
    constructor() {}

    /**
     * Set an application parameter.
     * @param key Key of the parameter.
     * @param value Value of the parameter.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // set an application parameter
     * DMS.ApplicationParameter.set('param1', 'test');
     * ```
     */
    public static set(key: string, value: any): void {
        this._applicationParameters[key.toLowerCase()] = value;
    }

    /**
     * Get a single parameter.
     * @param key Key of the parameter.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // get an application parameter
     * var param = DMS.ApplicationParameter.get('param1');
     * ```
     */
    public static get(key: string): any {
        let value = this._applicationParameters[key.toLowerCase()];

        if (value == null) {
            // Support legacy @@prm:xx@@ where it also looked in QueryParameters.
            value = QueryParameter.get(key);
        }
        if (value == null) {
            let params = PageContext.getFilterParameters();
            if (params && params.hasOwnProperty(key)) {
                value = params[key];
            }
        }
        return value;
    }

    /**
     * Remove a single parameter.
     * @param key Key of the parameter.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // remove an application parameter
     * DMS.ApplicationParameter.remove('param1');
     * ```
     */
    public static remove(key: string): void {
        delete this._applicationParameters[key.toLowerCase()];
    }

    /**
     * Set all parameters on initialization.
     * @param parameters All parameters.
     * @hidden
     */
    public static initialize(parameters: IStringDictionary<any>): void {
        this._applicationParameters = $.extend(this._applicationParameters, parameters);
    }

    /**
     * Gets all parameters.
     * @hidden
     */
    public static getAll(): IStringDictionary<any> {
        return this._applicationParameters;
    }

    /**
     * @hidden
     */
    public static clear(): void {
        this._applicationParameters = {};
    }
}
